/**
 * TSUNAMI — Product Recommendation Quiz
 * Asset: assets/tsunami-quiz.js
 *
 * Flow:
 * 1. Modal opens → quiz starts immediately on first question
 * 2. JS fetches products via Shopify Storefront API (GraphQL)
 * 3. Sends answers + products to Cloudflare Worker
 * 4. Worker returns ranked recommendations
 * 5. JS renders product cards
 */

(function () {
  "use strict";

  //  CONFIG 

  /**
   * Quiz questions.
   * type: "single" | "multi"
   * id: used as the key in quizAnswers sent to the Worker
   *
   * Format question removed — only flower/bud is currently available,
   * so asking is redundant and hurts completion rate.
   */
  const QUESTIONS = [
    {
      id: "experience",
      text: "How would you describe your cannabis experience?",
      type: "single",
      options: [
        { value: "new",        label: "Just starting out",        emoji: "🌱" },
        { value: "occasional", label: "I use it occasionally",    emoji: "🌊" },
        { value: "regular",    label: "Regular user",             emoji: "🏄" },
        { value: "seasoned",   label: "Very experienced / daily", emoji: "⚡" },
      ],
    },
    {
      id: "goal",
      text: "What kind of vibe are you going for?",
      type: "single",
      options: [
        { value: "relax",   label: "Chill & unwind",                      emoji: "😌" },
        { value: "sleep",   label: "Wind down for sleep",                  emoji: "💤" },
        { value: "focus",   label: "Get in a creative or focused mood",    emoji: "🎯" },
        { value: "social",  label: "Something uplifting for social time",  emoji: "✨" },
      ],
    },
    {
      id: "price",
      text: "Any price preference?",
      type: "single",
      options: [
        { value: "Deals only",  label: "On Sale Only",   emoji: "💰" },
        { value: "Mid-price",   label: "Balanced",       emoji: "⚖️" },
        { value: "Top Shelf",  label: "Top Shelf Product", emoji: "🌊" },
      ],
    },
  ];

  const LOADING_MESSAGES = [
    "Analyzing your preferences…",
    "Matching you with the right wave…",
    "Curating your lineup…",
    "Almost there — finding your perfect match…",
  ];

  //  STOREFRONT API QUERY 

  const PRODUCTS_QUERY = /* GraphQL */ `
    query GetProducts($limit: Int!) {
      products(first: $limit, sortKey: BEST_SELLING) {
        edges {
          node {
            id
            handle
            title
            productType
            description(truncateAt: 300)
            availableForSale
            tags
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
          }
        }
      }
    }
  `;

  //  STATE ─

  const state = {
    currentQuestion: 0,
    answers:  {},
    products: [],
    results:  [],
  };

  //  DOM ─

  const root = document.getElementById("tsunami-quiz");
  if (!root) return;

  const WORKER_URL         = root.dataset.workerUrl || "https://tsu.emmanuel234432.workers.dev";
  const PRODUCT_LIMIT      = parseInt(root.dataset.productLimit, 10) || 50;
  const STOREFRONT_TOKEN   = root.dataset.storefrontToken || "a25c8a108897c750039d6b2d1c99a2e3";
  const SHOP_DOMAIN        = window.Shopify?.shop || window.location.hostname;

  //  HELPERS ─

  function showScreen(name) {
    root.querySelectorAll(".quiz-screen").forEach((s) => s.classList.remove("is-active"));
    const target = root.querySelector(`[data-screen="${name}"]`);
    if (target) target.classList.add("is-active");
  }

  function updateProgress() {
    const total   = QUESTIONS.length;
    const current = state.currentQuestion + 1;
    const pct     = Math.round((state.currentQuestion / total) * 100);
    const bar     = root.querySelector(".js-progress-bar");
    const label   = root.querySelector(".js-progress-label");
    if (bar)   bar.style.setProperty("--progress", pct + "%");
    if (label) label.textContent = `Question ${current} of ${total}`;
  }

  function hasAnswered(q) {
    const a = state.answers[q.id];
    if (q.type === "multi") return Array.isArray(a) && a.length > 0;
    return !!a;
  }

  //  RENDER QUESTION ─

  function renderQuestion(index) {
    const q       = QUESTIONS[index];
    const wrap    = root.querySelector(".js-question-wrap");
    const nextBtn = root.querySelector(".js-quiz-next");
    const backBtn = root.querySelector(".js-quiz-back");

    const isFirst = index === 0;
    const isLast  = index === QUESTIONS.length - 1;

    if (backBtn) backBtn.style.visibility = isFirst ? "hidden" : "visible";
    if (nextBtn) nextBtn.textContent = isLast ? "See My Picks →" : "Next →";

    const optionsHtml = q.options
      .map((opt) => {
        const inputType = q.type === "multi" ? "checkbox" : "radio";
        const inputId   = `quiz-opt-${q.id}-${opt.value}`;
        return /* html */ `
          <li class="quiz-option">
            <input
              type="${inputType}"
              id="${inputId}"
              name="quiz-${q.id}"
              value="${opt.value}"
            />
            <label for="${inputId}">
              ${opt.emoji ? `<span class="quiz-option__emoji" aria-hidden="true">${opt.emoji}</span>` : ""}
              ${opt.label}
              <span class="quiz-option__check" aria-hidden="true"></span>
            </label>
          </li>
        `;
      })
      .join("");

    wrap.innerHTML = /* html */ `
      <div class="quiz-question" role="group" aria-labelledby="q-label-${q.id}">
        <p class="quiz-question__text" id="q-label-${q.id}">${q.text}</p>
        <ul class="quiz-options">${optionsHtml}</ul>
      </div>
    `;

    // Re-apply saved answers
    const saved = state.answers[q.id];
    if (saved) {
      const vals = Array.isArray(saved) ? saved : [saved];
      vals.forEach((v) => {
        const input = wrap.querySelector(`[value="${v}"]`);
        if (input) input.checked = true;
      });
    }

    updateNextBtn();

    wrap.querySelectorAll("input").forEach((inp) => {
      inp.addEventListener("change", () => {
        if (q.type === "multi") {
          state.answers[q.id] = Array.from(
            wrap.querySelectorAll("input:checked")
          ).map((i) => i.value);
        } else {
          state.answers[q.id] = inp.value;
        }
        updateNextBtn();
      });
    });

    updateProgress();
  }

  function updateNextBtn() {
    const q       = QUESTIONS[state.currentQuestion];
    const nextBtn = root.querySelector(".js-quiz-next");
    if (nextBtn) nextBtn.disabled = !hasAnswered(q);
  }

  //  INIT — start on first question immediately 

  function initQuiz() {
    state.currentQuestion = 0;
    state.answers  = {};
    state.results  = [];
    showScreen("questions");
    renderQuestion(0);
  }

  // Run as soon as the DOM element exists (deferred script)
  initQuiz();

  //  NAVIGATION 

  root.querySelector(".js-quiz-next")?.addEventListener("click", () => {
    const isLast = state.currentQuestion === QUESTIONS.length - 1;
    if (isLast) {
      submitQuiz();
    } else {
      state.currentQuestion++;
      renderQuestion(state.currentQuestion);
    }
  });

  root.querySelector(".js-quiz-back")?.addEventListener("click", () => {
    if (state.currentQuestion > 0) {
      state.currentQuestion--;
      renderQuestion(state.currentQuestion);
    }
  });

  root.querySelectorAll(".js-quiz-retake").forEach((btn) => {
    btn.addEventListener("click", initQuiz);
  });

  //  SUBMIT 

  async function submitQuiz() {
    showScreen("loading");
    startLoadingMessages();

    try {
      const products = await fetchProducts();
      state.products = products;

      console.log("ANSWERS:", state.answers);
      console.log("PRODUCTS:", products);

      const recommendations = await fetchRecommendations(state.answers, products);
      state.results = recommendations;

      renderResults(recommendations, products);
      showScreen("results");
    } catch (err) {
      console.error("[Tsunami Quiz] Error:", err);
      const errMsg = root.querySelector(".js-error-msg");
      if (errMsg) errMsg.textContent = err.message || "Unexpected error. Please try again.";
      showScreen("error");
    }
  }

  //  STOREFRONT API 

  async function fetchProducts() {
    const endpoint = `https://${SHOP_DOMAIN}/api/2024-01/graphql.json`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: PRODUCTS_QUERY,
        variables: { limit: PRODUCT_LIMIT },
      }),
    });

    if (!res.ok) throw new Error("Failed to load product catalog.");

    const { data, errors } = await res.json();
    if (errors?.length) throw new Error("Shopify API error: " + errors[0].message);

    return (data?.products?.edges ?? []).map((edge) => ({
      ...edge.node,
      image: edge.node.images?.edges?.[0]?.node ?? null,
    }));
  }

  //  CLOUDFLARE WORKER ─

  async function fetchRecommendations(quizAnswers, products) {
    if (!WORKER_URL) throw new Error("Quiz worker URL is not configured.");

    const res = await fetch(WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quizAnswers, products }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Recommendation service unavailable.");
    }

    const { recommendations } = await res.json();
    if (!Array.isArray(recommendations) || recommendations.length === 0) {
      throw new Error("No recommendations returned. Please try again.");
    }

    return recommendations;
  }

  //  RENDER RESULTS 

  function renderResults(recommendations, allProducts) {
    const grid = root.querySelector(".js-results-grid");
    if (!grid) return;

    const productMap = Object.fromEntries(allProducts.map((p) => [p.handle, p]));

    const cards = recommendations
      .map((rec, i) => {
        const product = productMap[rec.handle];
        if (!product) return "";

        const price    = product.priceRange?.minVariantPrice;
        const priceStr = price
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: price.currencyCode,
            }).format(price.amount)
          : "";

        const imgHtml = product.image
          ? `<img src="${product.image.url}" alt="${product.image.altText || product.title}" loading="lazy" width="400" height="400" />`
          : `<div style="width:100%;height:100%;background:var(--quiz-off-white);display:flex;align-items:center;justify-content:center;color:var(--quiz-gray);font-size:2rem;">🌊</div>`;

        const badgeLabel = i === 0 ? "Best Match" : `#${i + 1} Pick`;

        return /* html */ `
          <a
            class="quiz-product-card"
            href="/products/${product.handle}"
            role="listitem"
            aria-label="${product.title} — ${rec.primaryBenefit}"
          >
            <span class="quiz-product-card__badge">${badgeLabel}</span>
            <div class="quiz-product-card__img-wrap">${imgHtml}</div>
            <div class="quiz-product-card__body">
              <span class="quiz-product-card__benefit">${rec.primaryBenefit || ""}</span>
              <h3 class="quiz-product-card__title">${product.title}</h3>
              <p class="quiz-product-card__reason">${rec.reason}</p>
              ${priceStr ? `<p class="quiz-product-card__price">${priceStr}</p>` : ""}
            </div>
            <span class="quiz-product-card__cta" aria-hidden="true">View Product</span>
          </a>
        `;
      })
      .filter(Boolean)
      .join("");

    grid.innerHTML = cards || "<p>No matching products found.</p>";
  }

  //  LOADING MESSAGES 

  let _loadingInterval;

  function startLoadingMessages() {
    let i  = 0;
    const el = root.querySelector(".js-loading-text");
    if (!el) return;
    el.textContent = LOADING_MESSAGES[0];
    clearInterval(_loadingInterval);
    _loadingInterval = setInterval(() => {
      i = (i + 1) % LOADING_MESSAGES.length;
      el.textContent = LOADING_MESSAGES[i];
    }, 2200);
  }

  // Clear interval when loading screen is no longer active
  new MutationObserver(() => {
    if (!root.querySelector(".quiz-screen--loading.is-active")) {
      clearInterval(_loadingInterval);
    }
  }).observe(root, { subtree: true, attributeFilter: ["class"] });

})();
